import React from 'react';

/**
 * Full per-item detail: decoration spec, placements (what goes where on the
 * garment), configured sizes/colors, pack unit selections, logo, notes.
 * Shared by the admin order page and the client portal.
 */

const human = (s) => String(s || '').replace(/_/g, ' ');

const Chip = ({ children }) => (
  <span className="inline-block px-2 py-0.5 mr-1 mb-1 text-xs rounded-full bg-[#4A90A4]/10 text-[#1B3A5C] font-medium">
    {children}
  </span>
);

const Row = ({ label, children }) => (
  <div className="flex flex-wrap gap-x-2 text-sm py-0.5">
    <span className="text-muted-foreground min-w-[90px] flex-shrink-0">{label}</span>
    <span className="text-foreground min-w-0 break-words">{children}</span>
  </div>
);

const OrderItemDetails = ({ item }) => {
  const opts = item.customization_options || {};
  const cfg = item.config || {};

  const placements = opts.decoration?.placements || (Array.isArray(opts.placements) ? opts.placements : []);
  // Exact size+color pairs from the quote builder (preferred display)
  const unitPairs = Array.isArray(opts.size_color_breakdown)
    ? opts.size_color_breakdown.filter((p) => p && p.quantity > 0) : [];
  // Post-purchase config wins; otherwise totals chosen in the quote builder.
  // When exact pairs exist, the separate totals are redundant — hide them.
  const sizeBd = cfg.size_breakdown || (unitPairs.length ? {} : opts.size_breakdown) || {};
  const colorBd = cfg.color_breakdown || (unitPairs.length ? {} : opts.color_breakdown) || {};
  // Pack selections: post-purchase config, or captured at add-to-cart (storefront)
  const bundle = (cfg.bundle_selections && Object.keys(cfg.bundle_selections).length
    ? cfg.bundle_selections : opts.bundle_selections) || {};
  const unitSelections = Array.isArray(bundle.item_selections) ? bundle.item_selections : [];
  const upgrades = Array.isArray(bundle.upgrades) ? bundle.upgrades : [];
  // Plain storefront garment selection
  const plainSel = [
    opts.fit && `Fit ${human(opts.fit)}`,
    opts.size && `Size ${opts.size}`,
    opts.color && String(opts.color),
  ].filter(Boolean);

  const decoBits = [
    (opts.decoration?.method || opts.decoration_method) && human(opts.decoration?.method || opts.decoration_method),
    opts.decoration_complexity && human(opts.decoration_complexity),
    opts.decoration_size && `${opts.decoration_size}"`,
  ].filter(Boolean);

  const garmentSource = opts.garment_source === 'byo' ? 'Customer-supplied garment'
    : opts.garment_source === 'shop' ? 'Shop garment' : null;

  const hasSizes = Object.keys(sizeBd).length > 0 || unitPairs.length > 0 || !!opts.size;
  const hasColors = Object.keys(colorBd).length > 0 || unitPairs.length > 0 || !!opts.color;
  const hasAnything = decoBits.length || placements.length || hasSizes || hasColors ||
    unitSelections.length || upgrades.length || garmentSource || cfg.logo_type ||
    item.customization_text || opts.product_code || plainSel.length || opts.school_name;

  // Post-purchase selections still owed on shop-garment items
  const missing = [];
  if (opts.garment_source !== 'byo' && !unitSelections.length && (!cfg.status || cfg.status !== 'complete')) {
    if (!hasSizes) missing.push('sizes');
    if (!hasColors) missing.push('colors');
  }
  const awaitingConfig = missing.length > 0;
  const missingLabel = missing.join(' & ').replace(/^./, (c) => c.toUpperCase());

  if (!hasAnything && !awaitingConfig) return null;

  return (
    <div className="mt-2 pl-3 border-l-2 border-[#4A90A4]/20 space-y-1" data-testid="order-item-details">
      {!!opts.pricing?.name_per_item && <p className="text-sm">Name personalization: +${Number(opts.pricing.name_per_item).toFixed(2)} per item</p>}
      {opts.decoration && <div className="space-y-2 text-sm">
        {opts.decoration.personal_name && <p>Name: {opts.decoration.personal_name}</p>}
        {opts.decoration.placements.map(p => <div key={p.location}>
          <strong>{p.label || human(p.location)}{p.included ? ' (included)' : ''}</strong>
          <p>{[p.text, p.font, p.thread_color, p.instructions].filter(Boolean).join(' · ')}</p>
          {p.artwork && <a className="underline" href={p.artwork.file_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${p.artwork.file_url}` : p.artwork.file_url} target="_blank" rel="noreferrer">{p.artwork.file_name}</a>}
        </div>)}
        {!!opts.pricing?.setup_fee && <p>Setup: ${Number(opts.pricing.setup_fee).toFixed(2)}</p>}
      </div>}

      {plainSel.length > 0 && <Row label="Selection">{plainSel.join(' · ')}</Row>}
      {opts.school_name && <Row label="School">{opts.school_name}</Row>}
      {garmentSource && <Row label="Garment">{garmentSource}{opts.product_code ? ` · ${opts.product_code}` : ''}</Row>}
      {!garmentSource && opts.product_code && <Row label="Product code">{opts.product_code}</Row>}
      {decoBits.length > 0 && <Row label="Decoration">{decoBits.join(' · ')}</Row>}

      {placements.length > 0 && !opts.decoration && (
        <div className="text-sm py-0.5">
          <span className="text-muted-foreground">Placements</span>
          <ul className="list-disc ml-6 mt-0.5 space-y-0.5">
            {placements.map((p, i) => {
              const bits = [
                p.size != null && `${p.size_range || p.size}″`,
                p.complexity && human(p.complexity),
                p.decoration_method && human(p.decoration_method),
                p.thread_color && `Thread: ${p.thread_color}`,
                (p.text || p.text_line) && `Text: "${p.text || p.text_line}"`,
              ].filter(Boolean);
              return (
                <li key={i}>
                  <span className="font-medium capitalize">{human(p.location || p.label || p.id || 'placement')}</span>
                  {bits.length > 0 && <span className="text-muted-foreground"> — {bits.join(' · ')}</span>}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {unitPairs.length > 0 && (
        <div className="text-sm py-0.5">
          <span className="text-muted-foreground">Units</span>
          <ul className="list-disc ml-6 mt-0.5 space-y-0.5">
            {unitPairs.map((p, i) => (
              <li key={i}>
                <span className="font-medium">
                  {[p.size, p.color && human(p.color).replace(/\b\w/g, (c) => c.toUpperCase())].filter(Boolean).join(' — ')}
                </span>
                <span className="text-muted-foreground"> × {p.quantity}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {Object.keys(sizeBd).length > 0 && (
        <Row label="Sizes">
          {Object.entries(sizeBd).map(([k, v]) => <Chip key={k}>{k} × {v}</Chip>)}
        </Row>
      )}
      {Object.keys(colorBd).length > 0 && (
        <Row label="Colors">
          {Object.entries(colorBd).map(([k, v]) => <Chip key={k}>{k} × {v}</Chip>)}
        </Row>
      )}

      {unitSelections.length > 0 && (
        <div className="text-sm py-0.5">
          <span className="text-muted-foreground">Pack items</span>
          <ul className="list-disc ml-6 mt-0.5 space-y-0.5">
            {unitSelections.map((s, i) => {
              const bits = [
                s.fit && human(s.fit),
                (s.size || s.size_code) && `Size ${s.size || s.size_code}`,
                s.color || s.color_code,
              ].filter(Boolean).join(' · ');
              return (
                <li key={s.key || i}>
                  {s.product_name && <span className="font-medium">{s.product_name}{bits ? ' — ' : ''}</span>}
                  {bits || (!s.product_name && '(not selected yet)')}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {upgrades.length > 0 && (
        <Row label="Upgrades">
          {upgrades.map((u, i) => <Chip key={i}>{typeof u === 'object' ? (u.label || u.name || '') : String(u)}</Chip>)}
        </Row>
      )}

      {cfg.logo_type && (
        <Row label="Logo">
          {human(cfg.logo_type)}
          {cfg.logo_url && (
            <> · <a href={cfg.logo_url} target="_blank" rel="noreferrer" className="text-[#4A90A4] hover:underline">view file</a></>
          )}
        </Row>
      )}

      {item.customization_text && <Row label="Notes">{item.customization_text}</Row>}

      {awaitingConfig && (
        <p className="text-xs text-amber-600 italic">
          {missingLabel} not selected yet — customer completes this in their portal.
        </p>
      )}
    </div>
  );
};

export default OrderItemDetails;
